import { Link, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { categories, categoryMap } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const categoryName = categoryMap[activeSlug] || '全部作品';

  const filteredArticles = useMemo(() => {
    if (activeSlug === 'all') return articles;
    return articles.filter((a) => a.category === activeSlug);
  }, [activeSlug]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - ${siteInfo.name}`,
    description: `大桥未久${categoryName}的完整列表，收录${filteredArticles.length}篇相关内容。`,
    url: `${siteInfo.url}/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${siteInfo.url}/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={categoryName}
        description={`大桥未久${categoryName}的完整列表，收录${filteredArticles.length}篇相关内容。包含作品解析、最新动态、粉丝资讯等。`}
        keywords={`大桥未久,大桥未久${categoryName},大桥未久作品,大桥未久资讯`}
        canonicalUrl={`${siteInfo.url}/category/${activeSlug}`}
        schemaData={categorySchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          <section className="py-8 md:py-12 px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-600 mt-2">
                共 {filteredArticles.length} 篇内容
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 p-1 bg-background-100 rounded-full w-fit">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-inbox-line text-3xl text-secondary-500"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i> {article.publishedAt}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {article.readTime} 分钟
                        </span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}